import React, { useEffect, useRef, useState } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Switch,
  FormControlLabel,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  MenuItem,
  Select,
  type SelectChangeEvent,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import api from '../../services/api';
import { getKsefAuthStatus, getKsefSettings, saveKsefSettings, checkKsefConnection, type KsefSettings, type KsefConnectionStatus } from '../../services/ksefService';
import {
  listBankAccounts,
  createBankAccount,
  updateBankAccount,
  deleteBankAccount,
  setPrimaryBankAccount,
  formatAccountNumber,
  type BankAccount,
} from '../../services/bankAccountService';

interface KsefSettingsDrawerProps {
  open: boolean;
  onClose: () => void;
  onConnectionStatusChange?: (status: KsefConnectionStatus) => void;
}

const tabSx = {
  fontFamily: 'Roboto, sans-serif',
  fontWeight: 400,
  fontSize: '16px',
  textTransform: 'none' as const,
  color: '#020242',
  minWidth: 'auto',
  maxWidth: 'none',
  px: 0,
  textAlign: 'left' as const,
  '&.Mui-selected': { fontWeight: 500, color: '#020242' },
};

const KsefSettingsDrawer: React.FC<KsefSettingsDrawerProps> = ({
  open,
  onClose,
  onConnectionStatusChange,
}) => {
  // --- Wspólny stan ---
  const [activeTab, setActiveTab] = useState(0);

  // --- KSeF ---
  const [authType, setAuthType] = useState<'token' | 'certificate'>('certificate');
  const [token, setToken] = useState('');
  const [certificateFile, setCertificateFile] = useState<File | null>(null);
  const [privateKeyFile, setPrivateKeyFile] = useState<File | null>(null);
  const [certificatePassword, setCertificatePassword] = useState('');
  const [hasCertificate, setHasCertificate] = useState(false);
  const [contextNip, setContextNip] = useState('');
  const [environment, setEnvironment] = useState<'demo' | 'production'>('demo');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<KsefConnectionStatus | null>(null);
  const pollTimerRef = useRef<number | null>(null);

  // --- Rachunki bankowe ---
  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
  const [bankLoading, setBankLoading] = useState(false);
  const [bankError, setBankError] = useState<string | null>(null);
  const [bankSuccess, setBankSuccess] = useState<string | null>(null);
  // Formularz dodawania/edycji
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formAccountNumber, setFormAccountNumber] = useState('');
  const [formBankName, setFormBankName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formCurrency, setFormCurrency] = useState('PLN');
  const [isAdding, setIsAdding] = useState(false);
  const [formSaving, setFormSaving] = useState(false);

  // Załaduj ustawienia KSeF przy otwarciu
  useEffect(() => {
    if (open) {
      loadSettings();
      checkConnection();
      loadBankAccounts();
    }
    return () => {
      if (pollTimerRef.current) {
        window.clearTimeout(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
  }, [open]);

  // --- KSeF logic ---
  const loadSettings = async () => {
    try {
      setLoading(true);
      const settings = await getKsefSettings();
      if (settings) {
        setAuthType(settings.authType || 'certificate');
        setToken(settings.token || '');
        setCertificatePassword((settings as any).certificatePassword || '');
        setContextNip((settings as any).contextNip || (settings as any).context_nip || '');
        setEnvironment(settings.environment || 'demo');
        const hasCert = settings.authType === 'certificate' && !!((settings as any).certificatePath || (settings as any).certificate_path);
        setHasCertificate(!!hasCert);
      }
    } catch (err: any) {
      console.error('Błąd ładowania ustawień KSEF:', err);
      setError('Nie udało się załadować ustawień');
    } finally {
      setLoading(false);
    }
  };

  const checkConnection = async () => {
    try {
      const status = await checkKsefConnection();
      setConnectionStatus(status);
      if (onConnectionStatusChange) {
        onConnectionStatusChange(status);
      }
      if (status.phase === 'pending' && status.referenceNumber) {
        schedulePoll();
      }
    } catch (err: any) {
      console.error('Błąd sprawdzania połączenia:', err);
    }
  };

  const schedulePoll = () => {
    if (pollTimerRef.current) {
      window.clearTimeout(pollTimerRef.current);
    }
    pollTimerRef.current = window.setTimeout(async () => {
      const status = await getKsefAuthStatus();
      setConnectionStatus(status);
      if (onConnectionStatusChange) onConnectionStatusChange(status);
      if (status.phase === 'pending') {
        schedulePoll();
      }
    }, 1500);
  };

  const handleCertificateFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) setCertificateFile(file);
  };

  const handlePrivateKeyFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) setPrivateKeyFile(file);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const settings: KsefSettings = {
        authType,
        environment,
      };

      if (authType === 'token') {
        if (!token.trim()) {
          setError('Token jest wymagany');
          return;
        }
        settings.token = token;
      } else {
        const isNewUpload = !!certificateFile;
        if (!isNewUpload && !hasCertificate) {
          setError('Plik certyfikatu jest wymagany przy pierwszej konfiguracji');
          return;
        }
        if (isNewUpload) {
          const certExt = (certificateFile!.name || '').toLowerCase().slice(-4);
          const isCrt = certExt === '.crt' || certificateFile!.name?.toLowerCase().endsWith('.pem');
          if (isCrt && !privateKeyFile) {
            setError('Dla pliku .crt wymagany jest plik klucza prywatnego (.key). Plik .crt zawiera tylko certyfikat – bez klucza nie można podpisać dokumentu.');
            return;
          }
          if (!isCrt && !certificatePassword.trim()) {
            setError('Hasło do certyfikatu .p12/.pfx jest wymagane');
            return;
          }
        }
        if (!contextNip.trim().replace(/\D/g, '').match(/^\d{10}$/)) {
          setError('NIP firmy (10 cyfr) jest wymagany przy uwierzytelnianiu certyfikatem');
          return;
        }
        settings.certificatePassword = certificatePassword;
        settings.contextNip = contextNip.trim().replace(/\D/g, '');
      }

      await saveKsefSettings(settings);

      if (authType === 'certificate' && certificateFile) {
        const formData = new FormData();
        formData.append('certificate', certificateFile);
        if (privateKeyFile) formData.append('privateKey', privateKeyFile);
        formData.append('password', certificatePassword);
        formData.append('contextNip', contextNip.trim().replace(/\D/g, ''));
        formData.append('environment', environment);

        await api.post('/ksef/upload-certificate', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        setHasCertificate(true);
      }

      setSuccess('Ustawienia zostały zapisane');
      
      setTimeout(() => {
        checkConnection();
      }, 500);
    } catch (err: any) {
      console.error('Błąd zapisywania ustawień:', err);
      setError(err.response?.data?.message || 'Nie udało się zapisać ustawień');
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    try {
      setLoading(true);
      setError(null);
      const status = await checkKsefConnection();
      setConnectionStatus(status);
      if (onConnectionStatusChange) {
        onConnectionStatusChange(status);
      }
      if (status.phase === 'pending') {
        setSuccess(null);
        if (status.referenceNumber) schedulePoll();
      } else if (status.connected) {
        setSuccess('Połączenie z KSEF działa poprawnie');
      } else {
        setError(status.error || status.message || 'Brak połączenia z KSEF');
      }
    } catch (err: any) {
      setError('Błąd sprawdzania połączenia');
    } finally {
      setLoading(false);
    }
  };

  // --- Rachunki bankowe logic ---
  const loadBankAccounts = async () => {
    try {
      setBankLoading(true);
      setBankError(null);
      const items = await listBankAccounts();
      setBankAccounts(items);
    } catch (err: any) {
      console.error('Błąd ładowania rachunków:', err);
      setBankError('Nie udało się załadować rachunków bankowych');
    } finally {
      setBankLoading(false);
    }
  };

  const resetBankForm = () => {
    setFormAccountNumber('');
    setFormBankName('');
    setFormDescription('');
    setFormCurrency('PLN');
    setEditingId(null);
    setIsAdding(false);
  };

  const handleStartAdd = () => {
    resetBankForm();
    setIsAdding(true);
  };

  const handleStartEdit = (account: BankAccount) => {
    setEditingId(account.id);
    setFormAccountNumber(formatAccountNumber(account.account_number));
    setFormBankName(account.bank_name || '');
    setFormDescription(account.description || '');
    setFormCurrency(account.currency || 'PLN');
    setIsAdding(false);
  };

  const handleCancelEdit = () => {
    resetBankForm();
  };

  // Formatowanie podczas wpisywania
  const handleAccountNumberChange = (value: string) => {
    // Pozwól tylko na cyfry i spacje
    const digitsOnly = value.replace(/[^\d]/g, '');
    // Ogranicz do 26 cyfr
    const limited = digitsOnly.slice(0, 26);
    // Formatuj: xx xxxx xxxx xxxx xxxx xxxx xxxx
    setFormAccountNumber(formatAccountNumber(limited.padEnd(limited.length, ' ').trimEnd()) || limited);
  };

  const handleSaveBankAccount = async () => {
    try {
      setFormSaving(true);
      setBankError(null);
      setBankSuccess(null);

      const cleanNumber = formAccountNumber.replace(/\s/g, '');
      if (cleanNumber.length !== 26) {
        setBankError('Numer rachunku musi składać się z 26 cyfr');
        setFormSaving(false);
        return;
      }

      if (editingId !== null) {
        await updateBankAccount(editingId, cleanNumber, formBankName, formDescription, formCurrency);
        setBankSuccess('Rachunek zaktualizowany');
      } else {
        await createBankAccount(cleanNumber, formBankName, formDescription, formCurrency);
        setBankSuccess('Rachunek dodany');
      }

      resetBankForm();
      await loadBankAccounts();
    } catch (err: any) {
      console.error('Błąd zapisywania rachunku:', err);
      setBankError(err.response?.data?.message || 'Błąd zapisywania rachunku');
    } finally {
      setFormSaving(false);
    }
  };

  const handleDeleteBankAccount = async (id: number) => {
    try {
      setBankError(null);
      await deleteBankAccount(id);
      setBankSuccess('Rachunek usunięty');
      await loadBankAccounts();
    } catch (err: any) {
      console.error('Błąd usuwania rachunku:', err);
      setBankError('Błąd usuwania rachunku');
    }
  };

  const handleSetPrimary = async (id: number) => {
    try {
      setBankError(null);
      const items = await setPrimaryBankAccount(id);
      setBankAccounts(items);
      setBankSuccess('Rachunek główny zmieniony');
    } catch (err: any) {
      console.error('Błąd ustawiania rachunku głównego:', err);
      setBankError('Błąd ustawiania rachunku głównego');
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: 920, maxWidth: '100vw', borderRadius: 0 } }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Nagłówek */}
        <Box sx={{ pl: 4, pr: 4, pt: '22px', pb: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 500, fontSize: '18px', color: '#2D2D32' }}>
            Ustawienia
          </Typography>
          <IconButton 
            onClick={onClose} 
            size="small"
            sx={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 30,
              minHeight: 30,
              '& > *': {
                pointerEvents: 'none',
              },
            }}
          >
            <Box 
              component="img" 
              src="/assets/close.svg" 
              alt="Zamknij" 
              sx={{ 
                width: 13, 
                height: 13, 
                display: 'block',
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                pointerEvents: 'none',
              }} 
            />
          </IconButton>
        </Box>

        {/* Tabs */}
        <Box sx={{ px: 4, mt: '36px' }}>
          <Tabs
            value={activeTab}
            onChange={(_e, newValue) => setActiveTab(newValue)}
            variant="standard"
            sx={{
              position: 'relative',
              '&::after': { content: '""', position: 'absolute', left: 0, right: 0, bottom: 0, height: '1px', bgcolor: '#EDEDED' },
              '& .MuiTabs-scroller': { pl: 0, ml: 0 },
              '& .MuiTabs-flexContainer': { justifyContent: 'flex-start', gap: 4 },
            }}
            TabIndicatorProps={{ sx: { height: 2, bgcolor: '#000000' } }}
          >
            <Tab label="KSeF" sx={tabSx} />
            <Tab label="Rachunki Unitresco" sx={tabSx} />
          </Tabs>
        </Box>

        {/* Zawartość */}
        <Box sx={{ flex: 1, overflowY: 'auto', px: 4, py: 3 }}>
          {/* ========== TAB 0: KSeF ========== */}
          {activeTab === 0 && (
            <>
              {loading && !connectionStatus ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <>
                  {/* Status połączenia */}
                  {connectionStatus && (
                    <Alert 
                      severity={connectionStatus.phase === 'pending' ? 'info' : connectionStatus.connected ? 'success' : 'error'} 
                      sx={{ mb: 3 }}
                    >
                      {connectionStatus.connected
                        ? `Połączenie z KSEF działa poprawnie${connectionStatus.lastCheck ? ` (sprawdzone: ${new Date(connectionStatus.lastCheck).toLocaleString('pl-PL')})` : ''}`
                        : connectionStatus.phase === 'pending'
                          ? (connectionStatus.message || 'Uwierzytelnianie w toku – sprawdzam status...')
                          : `Brak połączenia z KSEF${connectionStatus.error ? `: ${connectionStatus.error}` : (connectionStatus.message ? `: ${connectionStatus.message}` : '')}`}
                    </Alert>
                  )}

                  {/* Komunikaty */}
                  {error && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                      {error}
                    </Alert>
                  )}
                  {success && (
                    <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
                      {success}
                    </Alert>
                  )}

                  {/* Toggle Token / Certyfikat */}
                  <Box sx={{ mb: 4 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={authType === 'certificate'}
                          onChange={(e) => {
                            setAuthType(e.target.checked ? 'certificate' : 'token');
                            setError(null);
                            setSuccess(null);
                          }}
                        />
                      }
                      label={
                        <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '16px', color: '#2D2D32' }}>
                          {authType === 'token' ? 'Uwierzytelnianie tokenem' : 'Uwierzytelnianie certyfikatem'}
                        </Typography>
                      }
                    />
                  </Box>

                  {/* Środowisko */}
                  <Box sx={{ mb: 3 }}>
                    <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '14px', fontWeight: 500, color: '#2D2D32', mb: 1 }}>
                      Środowisko
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Button
                        variant={environment === 'demo' ? 'contained' : 'outlined'}
                        onClick={() => setEnvironment('demo')}
                        sx={{
                          textTransform: 'none',
                          fontFamily: 'Roboto, sans-serif',
                          fontSize: '14px',
                          ...(environment === 'demo' ? { backgroundColor: '#4040DB', '&:hover': { backgroundColor: '#1F1FB0' } } : {}),
                        }}
                      >
                        DEMO
                      </Button>
                      <Button
                        variant={environment === 'production' ? 'contained' : 'outlined'}
                        onClick={() => setEnvironment('production')}
                        sx={{
                          textTransform: 'none',
                          fontFamily: 'Roboto, sans-serif',
                          fontSize: '14px',
                          ...(environment === 'production' ? { backgroundColor: '#4040DB', '&:hover': { backgroundColor: '#1F1FB0' } } : {}),
                        }}
                      >
                        PRODUKCJA
                      </Button>
                    </Box>
                  </Box>

                  {/* Formularz Token */}
                  {authType === 'token' && (
                    <Box sx={{ mb: 3 }}>
                      <TextField
                        fullWidth
                        label="Token KSEF"
                        type="text"
                        value={token}
                        onChange={(e) => setToken(e.target.value)}
                        placeholder="Wprowadź token KSEF"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            fontFamily: 'Roboto, sans-serif',
                          },
                        }}
                      />
                      <Typography variant="caption" sx={{ mt: 1, display: 'block', color: '#666' }}>
                        Token KSEF można wygenerować w systemie KSEF po zalogowaniu się kwalifikowanym podpisem elektronicznym.
                      </Typography>
                    </Box>
                  )}

                  {/* Formularz Certyfikat */}
                  {authType === 'certificate' && (
                    <>
                      <Box sx={{ mb: 3 }}>
                        <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '14px', fontWeight: 500, color: '#2D2D32', mb: 1 }}>
                          Certyfikat
                        </Typography>
                        <input
                          type="file"
                          accept=".p12,.pfx,.crt,.pem"
                          onChange={handleCertificateFileChange}
                          style={{ display: 'none' }}
                          id="certificate-file-input"
                        />
                        <label htmlFor="certificate-file-input">
                          <Button
                            variant="outlined"
                            component="span"
                            sx={{ textTransform: 'none', fontFamily: 'Roboto, sans-serif', fontSize: '14px' }}
                          >
                            {certificateFile ? certificateFile.name : 'Wybierz certyfikat (.crt, .p12, .pfx)'}
                          </Button>
                        </label>
                        {certificateFile?.name?.toLowerCase().endsWith('.crt') && (
                          <>
                            <input
                              type="file"
                              accept=".key,.pem"
                              onChange={handlePrivateKeyFileChange}
                              style={{ display: 'none' }}
                              id="private-key-file-input"
                            />
                            <label htmlFor="private-key-file-input">
                              <Button
                                variant="outlined"
                                component="span"
                                sx={{ textTransform: 'none', fontFamily: 'Roboto, sans-serif', fontSize: '14px', ml: 1 }}
                              >
                                {privateKeyFile ? privateKeyFile.name : 'Klucz prywatny (.key)'}
                              </Button>
                            </label>
                          </>
                        )}
                        {certificateFile && (
                          <Typography variant="caption" sx={{ mt: 1, display: 'block', color: '#666' }}>
                            {certificateFile.name}
                            {certificateFile.name?.toLowerCase().endsWith('.crt') && privateKeyFile && ` + ${privateKeyFile.name}`}
                          </Typography>
                        )}
                        <Typography variant="caption" sx={{ mt: 0.5, display: 'block', color: '#888' }}>
                          Dla .crt wymagany jest plik .key (klucz prywatny z generowania CSR). Plik .crt zawiera tylko certyfikat.
                        </Typography>
                      </Box>

                      <Box sx={{ mb: 3 }}>
                        <TextField
                          fullWidth
                          label="NIP firmy (kontekst)"
                          value={contextNip}
                          onChange={(e) => setContextNip(e.target.value.replace(/\D/g, '').slice(0, 10))}
                          placeholder="10 cyfr NIP"
                          sx={{ '& .MuiOutlinedInput-root': { fontFamily: 'Roboto, sans-serif' } }}
                          helperText="NIP firmy, w imieniu której uwierzytelniasz się certyfikatem"
                        />
                      </Box>

                      <Box sx={{ mb: 3 }}>
                        <TextField
                          fullWidth
                          label="Hasło (dla .p12/.pfx lub szyfrowanego .key)"
                          type="password"
                          value={certificatePassword}
                          onChange={(e) => setCertificatePassword(e.target.value)}
                          placeholder="Opcjonalne dla .key, wymagane dla .p12/.pfx"
                          sx={{ '& .MuiOutlinedInput-root': { fontFamily: 'Roboto, sans-serif' } }}
                        />
                      </Box>
                    </>
                  )}

                  {/* Przyciski akcji */}
                  <Box sx={{ display: 'flex', gap: 2, mt: 4 }}>
                    <Button
                      variant="outlined"
                      onClick={handleTestConnection}
                      disabled={loading || connectionStatus?.phase === 'pending'}
                      sx={{
                        textTransform: 'none',
                        fontFamily: 'Roboto, sans-serif',
                        fontSize: '14px',
                      }}
                    >
                      {loading ? <CircularProgress size={20} /> : 'Testuj połączenie'}
                    </Button>
                    <Button
                      variant="contained"
                      onClick={handleSave}
                      disabled={saving}
                      sx={{
                        textTransform: 'none',
                        fontFamily: 'Roboto, sans-serif',
                        fontSize: '14px',
                        backgroundColor: '#4040DB',
                        '&:hover': { backgroundColor: '#1F1FB0' },
                      }}
                    >
                      {saving ? <CircularProgress size={20} color="inherit" /> : 'Zapisz ustawienia'}
                    </Button>
                  </Box>
                </>
              )}
            </>
          )}

          {/* ========== TAB 1: Rachunki Unitresco ========== */}
          {activeTab === 1 && (
            <>
              {/* Komunikaty */}
              {bankError && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setBankError(null)}>
                  {bankError}
                </Alert>
              )}
              {bankSuccess && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setBankSuccess(null)}>
                  {bankSuccess}
                </Alert>
              )}

              {bankLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <>
                  {/* Tabela rachunków */}
                  <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#F8F8FA' }}>
                          <TableCell sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 500, fontSize: '13px', color: '#2D2D32', width: 50 }}>
                            Typ
                          </TableCell>
                          <TableCell sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 500, fontSize: '13px', color: '#2D2D32' }}>
                            Numer rachunku
                          </TableCell>
                          <TableCell sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 500, fontSize: '13px', color: '#2D2D32', minWidth: 120 }}>
                            Nazwa banku
                          </TableCell>
                          <TableCell sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 500, fontSize: '13px', color: '#2D2D32' }}>
                            Opis
                          </TableCell>
                          <TableCell sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 500, fontSize: '13px', color: '#2D2D32', width: 80 }}>
                            Waluta
                          </TableCell>
                          <TableCell sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 500, fontSize: '13px', color: '#2D2D32', width: 110 }} align="right">
                            Akcje
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {bankAccounts.length === 0 && !isAdding && (
                          <TableRow>
                            <TableCell colSpan={6} align="center" sx={{ py: 4, color: '#999', fontFamily: 'Roboto, sans-serif', fontSize: '14px' }}>
                              Brak rachunków bankowych. Kliknij "Dodaj rachunek" aby dodać pierwszy.
                            </TableCell>
                          </TableRow>
                        )}
                        {bankAccounts.map((account) => (
                          editingId === account.id ? (
                            /* Wiersz edycji */
                            <TableRow key={account.id}>
                              <TableCell sx={{ py: 1 }}>
                                <Tooltip title={account.is_primary ? 'Rachunek główny' : 'Kliknij aby ustawić jako główny'}>
                                  <IconButton size="small" onClick={() => handleSetPrimary(account.id)}>
                                    {account.is_primary ? <StarIcon fontSize="small" sx={{ color: '#F5A623' }} /> : <StarBorderIcon fontSize="small" sx={{ color: '#B9B9B9' }} />}
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                              <TableCell sx={{ py: 1 }}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  value={formAccountNumber}
                                  onChange={(e) => handleAccountNumberChange(e.target.value)}
                                  placeholder="xx xxxx xxxx xxxx xxxx xxxx xxxx"
                                  sx={{
                                    '& .MuiOutlinedInput-root': {
                                      fontFamily: 'Roboto Mono, monospace',
                                      fontSize: '13px',
                                    },
                                  }}
                                />
                              </TableCell>
                              <TableCell sx={{ py: 1 }}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  value={formBankName}
                                  onChange={(e) => setFormBankName(e.target.value)}
                                  placeholder="Nazwa banku"
                                  sx={{
                                    '& .MuiOutlinedInput-root': {
                                      fontFamily: 'Roboto, sans-serif',
                                      fontSize: '13px',
                                    },
                                  }}
                                />
                              </TableCell>
                              <TableCell sx={{ py: 1 }}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  value={formDescription}
                                  onChange={(e) => setFormDescription(e.target.value)}
                                  placeholder="Opis rachunku"
                                  sx={{
                                    '& .MuiOutlinedInput-root': {
                                      fontFamily: 'Roboto, sans-serif',
                                      fontSize: '13px',
                                    },
                                  }}
                                />
                              </TableCell>
                              <TableCell sx={{ py: 1 }}>
                                <Select
                                  fullWidth
                                  size="small"
                                  value={formCurrency}
                                  onChange={(e: SelectChangeEvent) => setFormCurrency(e.target.value)}
                                  sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '13px' }}
                                >
                                  <MenuItem value="PLN">PLN</MenuItem>
                                  <MenuItem value="EUR">EUR</MenuItem>
                                  <MenuItem value="USD">USD</MenuItem>
                                  <MenuItem value="GBP">GBP</MenuItem>
                                  <MenuItem value="CHF">CHF</MenuItem>
                                  <MenuItem value="CZK">CZK</MenuItem>
                                  <MenuItem value="SEK">SEK</MenuItem>
                                  <MenuItem value="NOK">NOK</MenuItem>
                                  <MenuItem value="DKK">DKK</MenuItem>
                                </Select>
                              </TableCell>
                              <TableCell align="right" sx={{ py: 1 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                                  <Tooltip title="Zapisz">
                                    <IconButton size="small" onClick={handleSaveBankAccount} disabled={formSaving} color="primary">
                                      {formSaving ? <CircularProgress size={16} /> : <SaveIcon fontSize="small" />}
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Anuluj">
                                    <IconButton size="small" onClick={handleCancelEdit}>
                                      <CancelIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </TableCell>
                            </TableRow>
                          ) : (
                            /* Wiersz wyświetlania */
                            <TableRow key={account.id} hover>
                              <TableCell>
                                <Tooltip title={account.is_primary ? 'Rachunek główny' : 'Kliknij aby ustawić jako główny'}>
                                  <IconButton size="small" onClick={() => handleSetPrimary(account.id)} disabled={isAdding || editingId !== null}>
                                    {account.is_primary ? <StarIcon fontSize="small" sx={{ color: '#F5A623' }} /> : <StarBorderIcon fontSize="small" sx={{ color: '#B9B9B9' }} />}
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                              <TableCell sx={{ fontFamily: 'Roboto Mono, monospace', fontSize: '13px', color: '#2D2D32', letterSpacing: '0.5px' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  {formatAccountNumber(account.account_number)}
                                  {account.is_primary ? (
                                    <Box component="span" sx={{
                                      display: 'inline-block', px: 0.8, py: 0.15,
                                      bgcolor: '#E8F5E9', color: '#2E7D32', borderRadius: '4px',
                                      fontSize: '10px', fontWeight: 600, fontFamily: 'Roboto, sans-serif',
                                      lineHeight: 1.4, whiteSpace: 'nowrap',
                                    }}>
                                      GŁÓWNY
                                    </Box>
                                  ) : null}
                                </Box>
                              </TableCell>
                              <TableCell sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '13px', color: '#2D2D32' }}>
                                {account.bank_name || '—'}
                              </TableCell>
                              <TableCell sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '13px', color: '#2D2D32' }}>
                                {account.description || '—'}
                              </TableCell>
                              <TableCell sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '13px', color: '#2D2D32' }}>
                                {account.currency || 'PLN'}
                              </TableCell>
                              <TableCell align="right">
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                                  <Tooltip title="Edytuj">
                                    <IconButton size="small" onClick={() => handleStartEdit(account)} disabled={isAdding || editingId !== null}>
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Usuń">
                                    <IconButton size="small" onClick={() => handleDeleteBankAccount(account.id)} disabled={isAdding || editingId !== null} color="error">
                                      <DeleteOutlineIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </TableCell>
                            </TableRow>
                          )
                        ))}

                        {/* Wiersz dodawania */}
                        {isAdding && (
                          <TableRow>
                            <TableCell sx={{ py: 1 }} />
                            <TableCell sx={{ py: 1 }}>
                              <TextField
                                fullWidth
                                size="small"
                                value={formAccountNumber}
                                onChange={(e) => handleAccountNumberChange(e.target.value)}
                                placeholder="xx xxxx xxxx xxxx xxxx xxxx xxxx"
                                autoFocus
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    fontFamily: 'Roboto Mono, monospace',
                                    fontSize: '13px',
                                  },
                                }}
                              />
                            </TableCell>
                            <TableCell sx={{ py: 1 }}>
                              <TextField
                                fullWidth
                                size="small"
                                value={formBankName}
                                onChange={(e) => setFormBankName(e.target.value)}
                                placeholder="Nazwa banku"
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    fontFamily: 'Roboto, sans-serif',
                                    fontSize: '13px',
                                  },
                                }}
                              />
                            </TableCell>
                            <TableCell sx={{ py: 1 }}>
                              <TextField
                                fullWidth
                                size="small"
                                value={formDescription}
                                onChange={(e) => setFormDescription(e.target.value)}
                                placeholder="Opis rachunku"
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    fontFamily: 'Roboto, sans-serif',
                                    fontSize: '13px',
                                  },
                                }}
                              />
                            </TableCell>
                            <TableCell sx={{ py: 1 }}>
                              <Select
                                fullWidth
                                size="small"
                                value={formCurrency}
                                onChange={(e: SelectChangeEvent) => setFormCurrency(e.target.value)}
                                sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '13px' }}
                              >
                                <MenuItem value="PLN">PLN</MenuItem>
                                <MenuItem value="EUR">EUR</MenuItem>
                                <MenuItem value="USD">USD</MenuItem>
                                <MenuItem value="GBP">GBP</MenuItem>
                                <MenuItem value="CHF">CHF</MenuItem>
                                <MenuItem value="CZK">CZK</MenuItem>
                                <MenuItem value="SEK">SEK</MenuItem>
                                <MenuItem value="NOK">NOK</MenuItem>
                                <MenuItem value="DKK">DKK</MenuItem>
                              </Select>
                            </TableCell>
                            <TableCell align="right" sx={{ py: 1 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                                <Tooltip title="Zapisz">
                                  <IconButton size="small" onClick={handleSaveBankAccount} disabled={formSaving} color="primary">
                                    {formSaving ? <CircularProgress size={16} /> : <SaveIcon fontSize="small" />}
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Anuluj">
                                  <IconButton size="small" onClick={handleCancelEdit}>
                                    <CancelIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {/* Przycisk dodawania */}
                  {!isAdding && editingId === null && (
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={handleStartAdd}
                      sx={{
                        textTransform: 'none',
                        fontFamily: 'Roboto, sans-serif',
                        fontSize: '14px',
                        backgroundColor: '#4040DB',
                        '&:hover': { backgroundColor: '#1F1FB0' },
                      }}
                    >
                      Dodaj rachunek
                    </Button>
                  )}
                </>
              )}
            </>
          )}
        </Box>
      </Box>
    </Drawer>
  );
};

export default KsefSettingsDrawer;
